import { useEffect } from "react";

import ReconnectingEventSource from "reconnecting-eventsource";

import { useAuth } from "../contexts/authContext";

import { resolveUrl } from "./useHttpMethods";

// Use this hook in context providers, to maintain centralized control for many SSE connections needed.
// - for each defined ref in any context for the purpose of maintaining a SSE connection, we can have a unique SSE connection across the app.
// - connection (with auto reconnection) is maintained when url is available and there is no active connection. meaning:
//     - to connect in a certain time, when we are ready, make sure url is available only on that time, and is empty before that.
//     - to disconnect in a certain time, make sure url becomes empty again on that time.
// - readyState of the underlying es object is state that is not managed by react, but we also should not manage it, since if we do, we have to store
// it in a context (near its ref), and because of auto re-connections, it'll cause too many re-rendering of the whole app. also a good ux would not
// rely on representing a flickering underlying connection state. so we should not use readyState as a state in react. and for sending messages, the
// reconnecting es lib, handles that by queueing messages when connection is not ready, and sending them when it is ready.
export default function useSSE(eventSourceRef, url, eventHandlers = {}) {
  const {
    userData: { accessToken },
  } = useAuth();

  useEffect(() => {
    if (!url && eventSourceRef.current) {
      // we were connected initially but now we don't want to be, since an empty url means we should not be connected
      console.log("SSE: (no-longer needed) unsubscribe from", url, eventSourceRef.current);
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      return;
    }

    if (!url || !accessToken || eventSourceRef.current) return; // not ready to connect yet, or already connected

    const es = new ReconnectingEventSource(`${resolveUrl(url)}?token=${accessToken}`);
    eventSourceRef.current = es;
    console.log("SSE: subscribing to", url, es);

    updateEventListeners(es, eventHandlers);

    es.onmessage = (event) => {
      console.log("SSE: onmessage", event);
    };
    es.onopen = (event) => {
      console.log("SSE: onopen", event);
    };
    es.onerror = (event) => {
      console.log("SSE: onerror", event);
    };
  }, [url, accessToken]);

  useEffect(() => {
    updateEventListeners(eventSourceRef.current, eventHandlers);
  }, [eventHandlers]);

  useEffect(
    () => () => {
      const es = eventSourceRef.current;
      console.log("SSE: (utilizer component unmount) unsubscribe from", url, es);
      if (es) {
        es.close();
        eventSourceRef.current = null;
      }
    },
    [],
  );
}

function updateEventListeners(es, eventHandlers) {
  if (!es || !eventHandlers) return;
  if (!es._eventListeners) es._eventListeners = {};

  for (const [eventName, handler] of Object.entries(eventHandlers)) {
    if (es._eventListeners[eventName]) es.removeEventListener(eventName, es._eventListeners[eventName]);

    es._eventListeners[eventName] = (event) => {
      handler(JSON.parse(event.data || "{}"), event);
    };
    es.addEventListener(eventName, es._eventListeners[eventName]);
  }
}
